import React, { useState, useEffect } from 'react';
import {
  Users, Crown, Shield, Edit3, User, MoreHorizontal,
  Search, Filter, Plus, Calendar, Activity, Award
} from 'lucide-react';
import { cmsFetch } from '../lib/cmsApi';
import CMSAuth from '../components/CMSAuth';

const CMSUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roleCounts, setRoleCounts] = useState({});
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    role: ''
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    if (hasAccess) fetchUsers();
  }, [hasAccess, filters]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      const response = await cmsFetch(`/api/cms/users?${params}`);

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setRoleCounts(data.role_counts || {});
        setTotalUsers(data.total || 0);
      } else {
        console.error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleUpdate = async () => {
    if (!selectedUser || !newRole) return;

    try {
      const response = await cmsFetch(`/api/cms/users/${selectedUser.user_id}/role`, {
        method: 'PUT',
        body: JSON.stringify({
          new_role: newRole,
          permissions: permissions
        }),
      });

      if (response.ok) {
        setShowRoleModal(false);
        setSelectedUser(null);
        setNewRole('');
        setPermissions([]);
        fetchUsers();
      } else {
        console.error('Failed to update user role');
      }
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    setNewRole(user.cms_role);
    setPermissions(user.permissions || []);
    setShowRoleModal(true);
  };

  const RoleBadge = ({ role }) => {
    const roleConfig = {
      super_admin: { color: 'purple', icon: Crown, label: 'Super Admin' },
      admin: { color: 'red', icon: Shield, label: 'Admin' },
      editor: { color: 'blue', icon: Edit3, label: 'Editor' },
      contributor: { color: 'green', icon: User, label: 'Contributor' },
      member: { color: 'gray', icon: User, label: 'Member' },
      user: { color: 'gray', icon: User, label: 'User' }
    };

    const config = roleConfig[role] || roleConfig.user;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
        ${config.color === 'purple' ? 'bg-purple-100 text-purple-800' :
          config.color === 'red' ? 'bg-red-100 text-red-800' :
          config.color === 'blue' ? 'bg-blue-100 text-blue-800' :
          config.color === 'green' ? 'bg-green-100 text-green-800' :
          'bg-gray-100 text-gray-800'}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const PermissionBadge = ({ permission }) => (
    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
      {permission.replace('_', ' ')}
    </span>
  );

  const UserRow = ({ user }) => {
    const displayName = user.display_name
      ? user.display_name.split(' ')[0]
      : (user.email ? user.email.split('@')[0] : 'User');
    const initial = displayName[0]?.toUpperCase() || 'U';
    const truncatedId = user.user_id.slice(0, 8) + '...';

    return (
    <tr className="bg-white hover:bg-gray-50">
      <td className="px-6 py-4">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-9 w-9">
            <div className="h-9 w-9 rounded-full bg-blue-500 flex items-center justify-center">
              <span className="text-sm font-medium text-white">{initial}</span>
            </div>
          </div>
          <div className="ml-3">
            <div className="text-sm font-medium text-gray-900">{displayName}</div>
            <div className="text-xs text-gray-400 font-mono">{truncatedId}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
        {user.email || '—'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <RoleBadge role={user.cms_role} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {user.walks_created}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {new Date(user.created_at).toLocaleDateString()}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {new Date(user.last_activity).toLocaleDateString()}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <button
          onClick={() => openRoleModal(user)}
          className="text-blue-600 hover:text-blue-900"
        >
          Edit Role
        </button>
      </td>
    </tr>
  );};

  const availablePermissions = [
    'create_walks',
    'edit_walks', 
    'delete_walks',
    'approve_walks',
    'manage_users',
    'bulk_operations',
    'analytics',
    'media_management'
  ];

  if (!hasAccess) {
    return <CMSAuth onAuthenticated={() => setHasAccess(true)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
              <p className="text-sm text-gray-500">Manage CMS user roles and permissions</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-xl font-semibold text-gray-900">{totalUsers}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {[
            { label: 'Super Admins', key: 'super_admin', color: 'purple', icon: Crown },
            { label: 'Admins', key: 'admin', color: 'red', icon: Shield },
            { label: 'Editors', key: 'editor', color: 'blue', icon: Edit3 },
            { label: 'Contributors', key: 'contributor', color: 'green', icon: User },
            { label: 'Members', key: 'member', color: 'gray', icon: User },
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className={`flex-shrink-0 p-3 rounded-lg bg-${stat.color}-100`}>
                  <stat.icon className={`w-6 h-6 text-${stat.color}-600`} />
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500">{stat.label}</h3>
                  <p className="text-2xl font-semibold text-gray-900">{roleCounts[stat.key] || 0}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <select
              value={filters.role}
              onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
              className="rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="editor">Editor</option>
              <option value="contributor">Contributor</option>
              <option value="member">Member</option>
              <option value="user">User</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Walks</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-gray-500">Loading users...</p>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>No users found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  users.map(user => <UserRow key={user.id} user={user} />)
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Role Update Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Update User Role
              </h3>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  User: <span className="font-medium">{selectedUser?.user_id}</span>
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  Current Role: <RoleBadge role={selectedUser?.cms_role} />
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Role
                </label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Role</option>
                  <option value="super_admin">Super Admin</option>
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="contributor">Contributor</option>
                  <option value="member">Member</option>
                  <option value="user">User</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Permissions
                </label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {availablePermissions.map(permission => (
                    <label key={permission} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={permissions.includes(permission)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setPermissions([...permissions, permission]);
                          } else {
                            setPermissions(permissions.filter(p => p !== permission));
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                      />
                      <span className="text-sm text-gray-700">{permission.replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRoleModal(false);
                    setSelectedUser(null);
                    setNewRole('');
                    setPermissions([]);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRoleUpdate}
                  disabled={!newRole}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Update Role
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CMSUserManagement;